import * as React from 'react';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { AccentButton } from '../AccentButton';
import { showDialog, t } from '../../services/HelperService';
import { reportAssistantBug } from '../customizations/customizationApi';
import { safeString } from './useAiChatAssistant';

/**
 * "Send Chat as Bug" prompt: collects the user's description of what went wrong.
 * Resolves { canceled: false, description } on send.
 */
const ReportAssistantBugDlg = ({ onClose }) => {
  const [description, setDescription] = React.useState('');

  const doSend = (e) => {
    onClose({ canceled: false, description: description.trim() });
    e?.promise?.();
  };

  return (
    <AccentDialog
      headerContent={t('Send Chat as Bug')}
      onClose={onClose}
      maxWidth={640}
      actionsContent={[
        <AccentButton key="send" tagName="sendBugReport" disabled={!description.trim()} onClick={doSend}>
          {t('Send')}
        </AccentButton>,
      ]}
    >
      <div style={{ display: 'grid', gap: 10 }}>
        <div style={{ fontSize: 13, color: '#475569' }}>
          {t('Describe what went wrong. The chat transcript and a diagnostic log of this session will be emailed to your support contact.')}
        </div>
        <textarea
          className="form-control"
          rows={5}
          maxLength={4000}
          autoFocus
          placeholder={t('What did you expect, and what happened instead?')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </AccentDialog>
  );
};

/**
 * The full "Send Chat as Bug" flow, shared by every assistant client: prompt for a
 * description, then email the transcript plus the server's diagnostic capture
 * (correlated by diagnosticSessionID — the id every chat turn already sends) to the
 * tenant's support address. The outcome lands in the chat via pushMsg, so the user
 * sees it in place. onSending (optional) fires after the user confirms, for callers
 * that show their own busy state during the send.
 */
export const promptAndReportAssistantBug = async ({ diagnosticSessionID, hostLabel, chatMessages, pushMsg, onSending }) => {
  try {
    const answer = await showDialog(<ReportAssistantBugDlg />);
    if (answer?.canceled) return;

    onSending?.();
    const transcript = (Array.isArray(chatMessages) ? chatMessages : [])
      .map((m) => `[${safeString(m.ts)}] ${safeString(m.role).toUpperCase()}: ${safeString(m.text)}`)
      .join('\n\n');
    const result = await reportAssistantBug({
      diagnosticSessionID,
      description: answer?.description ?? '',
      hostLabel: safeString(hostLabel),
      transcript,
    });
    if (result.sent) {
      pushMsg('system', t('The bug report was sent to your support contact. Thank you.'));
    } else {
      pushMsg('error', result.error || t('Sending the bug report failed.'));
    }
  } catch {
    pushMsg('error', t('Sending the bug report failed.'));
  }
};
